/**
 * PageErrorBoundary — iter867.
 *
 * Catches uncaught render errors in any child subtree and shows a
 * visible fallback instead of a blank white page. Critical for the
 * Android APK where a JS crash otherwise leaves the operator with no
 * feedback — they screenshot the fallback + we get the exact error.
 *
 * Usage:
 *   <PageErrorBoundary pageName="Printer Settings">
 *     <PrintersSettingsPage />
 *   </PageErrorBoundary>
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, err: error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log to console (accessible via adb logcat / chrome://inspect) so a
    // support engineer can pull the stack from a hooked-up device.
    console.error(`[PageErrorBoundary:${this.props.pageName}]`, error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = (this.state.err && (this.state.err.message || String(this.state.err))) || 'Unknown error';
    const stack = (this.state.err && this.state.err.stack) || '';
    const compStack = (this.state.info && this.state.info.componentStack) || '';
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="page-error-boundary">
        <div className="border border-error/40 bg-error/5 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-error mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-error mb-1">{this.props.pageName || 'Page'} failed to render</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Something threw an exception while building this page. Screenshot the box below and share it with support — the developer needs the stack to fix it.
              </p>
              <div className="bg-white/70 dark:bg-black/40 border border-error/20 rounded-lg p-3 space-y-2 font-mono text-[11px] overflow-x-auto">
                <div><span className="font-semibold text-error">Error:</span> {msg}</div>
                {stack && (
                  <details>
                    <summary className="cursor-pointer text-[10px] text-muted-foreground">JS stack (tap to expand)</summary>
                    <pre className="whitespace-pre-wrap mt-1 text-[10px]" data-testid="page-error-stack">{stack}</pre>
                  </details>
                )}
                {compStack && (
                  <details>
                    <summary className="cursor-pointer text-[10px] text-muted-foreground">React tree (tap to expand)</summary>
                    <pre className="whitespace-pre-wrap mt-1 text-[10px]" data-testid="page-error-tree">{compStack}</pre>
                  </details>
                )}
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90"
                  onClick={() => window.location.reload()}
                  data-testid="page-error-reload"
                >
                  <RefreshCw size={12} /> Reload page
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs hover:bg-secondary/50"
                  onClick={() => (window.location.href = '/')}
                  data-testid="page-error-home"
                >
                  Back to dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
